import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { RefreshCw, AlertCircle, CheckCircle2, XCircle, Mail, Clock, Repeat } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_META = {
  processed: { label: 'Processed', cls: 'bg-green-100 text-green-700 border-green-200', Icon: CheckCircle2 },
  skipped_status: { label: 'Unpaid / Cancelled', cls: 'bg-amber-100 text-amber-800 border-amber-300', Icon: AlertCircle },
  duplicate: { label: 'Duplicate', cls: 'bg-gray-100 text-gray-700 border-gray-200', Icon: Clock },
  no_cars: { label: 'No Cars Mapped', cls: 'bg-blue-50 text-blue-700 border-blue-200', Icon: AlertCircle },
  failed: { label: 'Failed', cls: 'bg-red-100 text-red-700 border-red-200', Icon: XCircle },
};

const FILTERS = [
  { value: 'all', label: 'All Logs' },
  { value: 'skipped_status', label: 'Unpaid / Cancelled (follow-up)' },
  { value: 'processed', label: 'Successfully Processed' },
  { value: 'failed', label: 'Failed' },
  { value: 'no_cars', label: 'No Cars Mapped' },
  { value: 'duplicate', label: 'Duplicates' },
];

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status || 'unknown', cls: 'bg-gray-100 text-gray-700 border-gray-200', Icon: AlertCircle };
  const Icon = meta.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs border ${meta.cls}`} data-testid={`webhook-status-${status}`}>
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
}

export default function WebhookLogsSection() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('skipped_status');
  const [retryingId, setRetryingId] = useState(null);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/webhook/logs?limit=200`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error('Error fetching webhook logs:', err);
      toast.error('Failed to load webhook logs');
    } finally {
      setLoading(false);
    }
  };

  const retryLog = async (log) => {
    if (!log?.id) return;
    const ok = window.confirm(
      `Force-process order #${log.order_id || ''}?\n\nThis will create the car registration even though the webhook arrived with a non-completed status. Only do this after you have confirmed the order is paid in WooCommerce.`
    );
    if (!ok) return;
    setRetryingId(log.id);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/webhook/logs/${log.id}/retry`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const result = res.data?.result || {};
      if (result.status === 'duplicate') {
        toast.info(`Order #${log.order_id} was already processed.`);
      } else if (result.cars_registered) {
        toast.success(`Created ${result.cars_registered} registration(s) for order #${log.order_id}.`);
      } else if (result.errors?.length) {
        toast.error(`No cars created: ${result.errors.join('; ')}`);
      } else {
        toast.message(`Retry completed (status: ${res.data?.log_status || 'unknown'}).`);
      }
      await fetchLogs();
    } catch (err) {
      console.error('Retry failed:', err);
      toast.error(err.response?.data?.detail || 'Retry failed');
    } finally {
      setRetryingId(null);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const filtered = useMemo(() => {
    if (filter === 'all') return logs;
    return logs.filter(l => (l.status || '').toLowerCase() === filter);
  }, [logs, filter]);

  const counts = useMemo(() => {
    const c = { all: logs.length, processed: 0, skipped_status: 0, failed: 0, no_cars: 0, duplicate: 0 };
    logs.forEach(l => {
      const s = (l.status || '').toLowerCase();
      if (c[s] !== undefined) c[s] += 1;
    });
    return c;
  }, [logs]);

  return (
    <div className="bg-white border border-gray-200 shadow-sm rounded-lg p-8" data-testid="webhook-logs-section">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Webhook Logs</h2>
          <p className="text-gray-600 text-sm">
            Review WooCommerce webhook activity. Use the &quot;Unpaid / Cancelled&quot; filter to spot abandoned checkouts and follow up with customers.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-[260px] bg-white border-gray-300" data-testid="webhook-logs-filter">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              {FILTERS.map(f => (
                <SelectItem key={f.value} value={f.value}>
                  {f.label}
                  {counts[f.value] !== undefined && (
                    <span className="ml-2 text-xs text-gray-500">({counts[f.value]})</span>
                  )}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={fetchLogs}
            disabled={loading}
            data-testid="webhook-logs-refresh"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary chips */}
      <div className="flex flex-wrap gap-2 mb-4 text-xs">
        <span className="px-2 py-1 rounded bg-gray-100 text-gray-700">Total: {counts.all}</span>
        <span className="px-2 py-1 rounded bg-green-100 text-green-700">Processed: {counts.processed}</span>
        <span className="px-2 py-1 rounded bg-amber-100 text-amber-800" data-testid="webhook-skipped-count">
          Unpaid / Cancelled: {counts.skipped_status}
        </span>
        <span className="px-2 py-1 rounded bg-red-100 text-red-700">Failed: {counts.failed}</span>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-gray-300 rounded-lg">
          <p className="text-gray-600">
            {loading ? 'Loading...' : filter === 'skipped_status'
              ? 'No abandoned or cancelled orders. Nice.'
              : 'No webhook logs match this filter.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="webhook-logs-table">
            <thead>
              <tr className="text-left text-gray-600 border-b border-gray-200">
                <th className="py-2 pr-4 font-medium">When</th>
                <th className="py-2 pr-4 font-medium">Order</th>
                <th className="py-2 pr-4 font-medium">Status</th>
                <th className="py-2 pr-4 font-medium">Customer</th>
                <th className="py-2 pr-4 font-medium">Cars</th>
                <th className="py-2 pr-4 font-medium">Notes</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(log => {
                const status = (log.status || '').toLowerCase();
                const rowHighlight = status === 'skipped_status'
                  ? 'bg-amber-50/40 hover:bg-amber-50'
                  : status === 'failed'
                  ? 'bg-red-50/40 hover:bg-red-50'
                  : 'hover:bg-gray-50';
                const followUpHref = log.customer_email
                  ? `mailto:${log.customer_email}?subject=${encodeURIComponent('Your Cruise 4 a Cause registration')}&body=${encodeURIComponent(`Hi ${log.customer_name || 'there'},\n\nWe noticed your registration order #${log.order_id} did not complete. Let us know if you would like help finishing your registration.\n\nThanks!`)}`
                  : null;
                return (
                  <tr key={log.id || `${log.order_id}-${log.processed_at}`} className={`border-b border-gray-100 ${rowHighlight}`} data-testid={`webhook-log-row-${log.order_id}`}>
                    <td className="py-2 pr-4 text-gray-700 whitespace-nowrap">{formatDate(log.processed_at)}</td>
                    <td className="py-2 pr-4 font-mono text-gray-900">#{log.order_id || '—'}</td>
                    <td className="py-2 pr-4"><StatusBadge status={status} /></td>
                    <td className="py-2 pr-4 text-gray-700">
                      <div className="font-medium text-gray-900">{log.customer_name || '—'}</div>
                      <div className="text-xs text-gray-500">{log.customer_email || '—'}</div>
                    </td>
                    <td className="py-2 pr-4 text-gray-700">{Array.isArray(log.token_ids) ? log.token_ids.length : 0}</td>
                    <td className="py-2 pr-4 text-gray-600 max-w-md">
                      <div className="text-xs">{log.event_type || '—'}</div>
                      {log.error && (
                        <div className="text-xs text-red-600 mt-0.5 break-words">{log.error}</div>
                      )}
                      {log.retried_at && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          Retried by {log.retried_by || 'admin'} at {formatDate(log.retried_at)}
                        </div>
                      )}
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        {status === 'skipped_status' && followUpHref && (
                          <a
                            href={followUpHref}
                            className="inline-flex items-center gap-1 text-xs text-pink-600 hover:text-pink-700"
                            data-testid={`webhook-followup-${log.order_id}`}
                          >
                            <Mail className="w-3 h-3" />
                            Email customer
                          </a>
                        )}
                        {['skipped_status', 'failed', 'no_cars'].includes(status) && log.raw_payload?.id && (
                          <button
                            type="button"
                            onClick={() => retryLog(log)}
                            disabled={retryingId === log.id}
                            className="inline-flex items-center gap-1 text-xs text-pink-600 hover:text-pink-700 disabled:opacity-50"
                            data-testid={`webhook-retry-${log.order_id}`}
                          >
                            <Repeat className={`w-3 h-3 ${retryingId === log.id ? 'animate-spin' : ''}`} />
                            {retryingId === log.id ? 'Retrying...' : 'Retry / Process now'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
